import React from 'react';
import { Box, Grid } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import SectionCard from '../../components/SectionCard';
import WorkflowPipeline from '../../components/WorkflowPipeline';
import QuickActionCard, { quickActionBreakpoints } from '../../components/QuickActionCard';
import useOverviewStats from '../../hooks/useOverviewStats';
import { INV_ICONS } from '../../constants/icons';
import { getPrescriptions, getControlledQueue, getWardReturns } from '../../api/pharmacy';
import { getPendingBilling } from '../../api/dispensing';
import { INV_SPACING } from '../../theme';

export default function PharmacyOverview() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const base = url.replace(/\/$/, '');

  const { stats, loading } = useOverviewStats({
    prescriptions: () => getPrescriptions({ status: 'ACTIVE' }),
    controlled: () => getControlledQueue({ status: 'PENDING' }),
    wardReturns: () => getWardReturns({ status: 'SUBMITTED' }),
    failedBilling: () => getPendingBilling({ billing_status: 'FAILED' }),
  });

  const STAT_CARDS = [
    { key: 'prescriptions', label: 'Active Prescriptions', icon: INV_ICONS.activeDispenses, tone: 'teal', path: `${base}/active-dispenses` },
    { key: 'controlled', label: 'Controlled Queue', icon: INV_ICONS.controlledQueue, tone: 'amber', path: `${base}/controlled-queue` },
    { key: 'wardReturns', label: 'Ward Returns Pending', icon: INV_ICONS.wardReturns, tone: 'mint', path: `${base}/ward-returns` },
    { key: 'failedBilling', label: 'Failed Billing', icon: INV_ICONS.bills, tone: 'red', path: `${base}/bills` },
  ];

  const QUICK_ACTIONS = [
    { title: 'IPD Dispense', description: 'Dispense medicines against inpatient prescriptions', icon: INV_ICONS.activeDispenses, path: `${base}/active-dispenses` },
    { title: 'Direct Dispense', description: 'Dispense to a walk-in or OPD patient without an IPD prescription', icon: INV_ICONS.directDispenses, path: `${base}/direct-dispenses` },
    { title: 'Bills', description: 'View and settle pharmacy bills', icon: INV_ICONS.bills, path: `${base}/bills` },
    { title: 'Refunds', description: 'Raise, action and settle pharmacy refunds', icon: INV_ICONS.refunds, path: `${base}/refunds` },
  ];
  const actionSpan = quickActionBreakpoints(QUICK_ACTIONS.length);

  const PIPELINE = [
    { label: 'Prescription', path: `${base}/active-dispenses` },
    { label: 'Dispense', path: `${base}/direct-dispenses` },
    { label: 'Bill', path: `${base}/bills` },
    { label: 'Return', path: `${base}/ward-returns` },
  ];

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <PageHeader
        breadcrumb={['Inventory Suite', 'Pharmacy Management']}
        title="Overview"
        description="Prescriptions, controlled substances, billing and returns at a glance"
      />

      <Grid container spacing={INV_SPACING.grid} sx={{ mb: INV_SPACING.section }}>
        {STAT_CARDS.map((card) => (
          <Grid item xs={6} md={3} key={card.key}>
            <StatCard
              label={card.label}
              value={stats[card.key]}
              icon={card.icon}
              tone={card.tone}
              loading={loading}
              onClick={() => history.push(card.path)}
            />
          </Grid>
        ))}
      </Grid>

      <SectionCard title="Dispensing Workflow" sx={{ mb: INV_SPACING.section }}>
        <WorkflowPipeline stages={PIPELINE} />
      </SectionCard>

      <SectionCard>
        <Grid container spacing={INV_SPACING.grid}>
          {QUICK_ACTIONS.map((action) => (
            <QuickActionCard
              key={action.title}
              icon={action.icon}
              title={action.title}
              description={action.description}
              onClick={() => history.push(action.path)}
              {...actionSpan}
            />
          ))}
        </Grid>
      </SectionCard>
    </Box>
  );
}
